import { createAction } from '@reduxjs/toolkit'

import { contentManagementActions } from 'apps/cms/store'
import { ContentManagementSystemSocketEvents } from 'apps/cms/store/lib/constants'
import { mapSocketFsFlowDeleted } from 'apps/cms/store/lib/lib'
import { SocketFsFlow, SocketFsFlowDeleted } from 'apps/cms/store/lib/types'
import ConvertAlert from 'common/cms/controllers/ConvertFlowModal/ConvertAlert'
import FSPath from 'common/cms/models/FSPath'
import { alert } from 'common/core'
import { convertFlowChannelsAdapter } from 'common/flow/flowHelpers'
import { ConvertChannelRecord } from 'common/flow/flowInterfaces'
import { parseFlow } from 'common/flow/models/Flow/parseFlow'
import * as flowSelectors from 'common/flow/selectors/flowSelectors'
import { LightFlow, Flow } from 'shared/api/common/schemas/flow'
import { CmsApi } from 'shared/api/requests/cms'
import { FlowApi } from 'shared/api/requests/flow'
import { createAsyncAction } from 'shared/lib/redux'
import { navigatePollyfill } from 'utils/router/tools'
import {
  anotherTabNotificationsListener,
  accountNotificationsListener,
} from 'utils/services/notificationsService'
import { linkURL } from 'utils/url'

export const fetchFlow = createAsyncAction(
  'flow/flowFetch',
  async (flowId: string | undefined, { getState }) => {
    if (!flowId) {
      return
    }

    const isFetching = flowSelectors.isFlowFetchingById(getState(), flowId)

    if (isFetching) {
      return
    }

    const item = flowSelectors.getById(getState(), flowId)

    if (item) {
      return item
    }

    const { data } = await FlowApi.getFlow({ query: { ns: flowId, content_holder: '' } })

    if (!data.flow) {
      return
    }

    return parseFlow(data.flow)
  },
)

export const fetchFullFlow = createAsyncAction(
  'flow/fetchFullFlow',
  async ({ flowId, chId }: { flowId: string; chId?: string | null }) => {
    if (!flowId) {
      return
    }

    const { data } = await FlowApi.getFlowData({
      query: {
        ns: flowId,
        // for retrieving aggregated stats content holder should be omitted, not empty, and not null
        ...(chId ? { content_holder: chId } : {}),
      },
    })

    if (!data.flow) {
      return
    }

    return parseFlow(data.flow)
  },
  {
    onBusinessError(error) {
      alert(error.original_message, 'danger')
      error.handle()
    },
  },
)

export const fetchFlowContentData = createAsyncAction(
  'flow/flowContentDataFetch',
  async ({ flowId }: { flowId?: string; flowName: string; flowPath: string }) => {
    if (!flowId) {
      return
    }

    const { data } = await FlowApi.getFlowContentData({ query: { ns: flowId } })

    if (!data) {
      return
    }

    return data.flow
  },
  {
    onError: (_, __, { dispatch }) => {
      dispatch(contentManagementActions.setLastSelectedFlowId(null))
    },
  },
)

export const updateFlowName = createAsyncAction(
  'flow/updateFlowName',
  async ({ flowId, name }: { flowId: string; name: string }) => {
    const { data } = await FlowApi.setFlowName({ body: { ns: flowId, name } })

    if (data?.flow) {
      return
    }

    return parseFlow(data.flow)
  },
)

export const updateFlowShared = createAsyncAction(
  'flow/updateFlowShared',
  async ({ flowId, shared }: { flowId: string; shared: boolean }) => {
    const { data } = await FlowApi.setFlowShared({
      body: {
        ns: flowId,
        shared,
      },
    })

    if (!data.flow) {
      return
    }

    return parseFlow(data.flow)
  },
)

export const updateFlowSharedCloning = createAsyncAction(
  'flow/updateFlowSharedCloning',
  async ({ flowId, shared }: { flowId: string; shared: boolean }) => {
    const { data } = await FlowApi.setFlowSharedCloning({
      body: {
        ns: flowId,
        shared_cloning_enabled: shared,
      },
    })

    if (!data.flow) {
      return
    }

    return parseFlow(data.flow)
  },
)

export const exportFlow = createAsyncAction(
  'flow/exportFlow',
  async ({
    flowId,
    destAccountId,
    mappingSettings,
  }: {
    flowId: string
    destAccountId: string
    mappingSettings?: Record<string, unknown>
  }) => {
    await CmsApi.exportFlow({
      body: {
        flow_ns: flowId,
        target_account_id: Number(destAccountId.replace('fb', '')),
        mapping_settings: mappingSettings || {},
      },
    })
  },
)

export const importSharedFlow = createAsyncAction(
  'flow/importSharedFlow',
  async ({ pageId, shareHash }: { pageId: string; shareHash?: string }, { rejectWithValue }) => {
    if (!shareHash || !pageId) {
      throw rejectWithValue()
    }

    const { data } = await FlowApi.importSharedFlow({
      path: { pageId },
      body: {
        share_hash: shareHash,
      },
    })

    return data
  },
)

export const fetchFlowDependencies = createAsyncAction(
  'flow/fetchFlowDependencies',
  async (flowId: string) => {
    if (!flowId) {
      return
    }
    const { data } = await CmsApi.fetchFlowDependencies({
      query: { flow_ns: flowId },
    })

    return data.map
  },
)

export const convertFlowContentChannels = createAsyncAction(
  'flow/convertFlowContentChannels',
  async ({ flowId, channels }: { flowId: string; channels: Partial<ConvertChannelRecord> }) => {
    const requestBody = convertFlowChannelsAdapter(flowId, channels)
    const { data } = await CmsApi.convertFlow({
      body: requestBody,
    })

    if (data.fs_object?.path) {
      const path = FSPath.getPathURL(data.fs_object.path)
      navigatePollyfill(path)
    }

    alert(ConvertAlert, 'success')
  },
)

export const flowPermanentlyRemoved = createAsyncAction(
  'flow/flowPermanentlyRemoved',
  async (path: string) => {
    if (window.location.pathname.endsWith(path)) {
      navigatePollyfill(linkURL('cms'))
      return
    }

    return path
  },
)

// Sync actions
export const resetFlow = createAction('flow/resetFlow', (flowData: LightFlow | Flow) => {
  return {
    payload: parseFlow(flowData),
  }
})

export const setFlowHasDraft = createAction(
  'flow/setFlowHasDraft',
  (flowId: string, hasDraft: boolean) => ({
    payload: {
      flowId,
      hasDraft,
    },
  }),
)

export const setDraftSuccess = createAction('flow/setDraftSuccess', (flowId: string) => ({
  payload: {
    flowId,
  },
}))

export const exportFlowComplete = createAction(
  'flow/exportFlowComplete',
  (flowId: string, destAccountId: string) => ({
    payload: {
      flowId,
      destAccountId,
    },
  }),
)

export const flowRemoved = createAction('flow/removed', (path: string) => ({
  payload: path,
}))

export const flowRestored = createAction('flow/restored', (path: string) => ({
  payload: path,
}))

anotherTabNotificationsListener.on(
  ContentManagementSystemSocketEvents.FLOW_PREVIEW_UPDATED,
  (data: { flow: LightFlow }, dispatch) => dispatch(resetFlow(data.flow)),
)

accountNotificationsListener.on(
  ContentManagementSystemSocketEvents.FS_FLOW_DELETED,
  (data: { flow: SocketFsFlow }, dispatch) => dispatch(flowRemoved(data.flow.path)),
)

accountNotificationsListener.on(
  ContentManagementSystemSocketEvents.FS_FLOW_RESTORED,
  (data: { flow: SocketFsFlow }, dispatch) => dispatch(flowRestored(data.flow.path)),
)

accountNotificationsListener.on(
  ContentManagementSystemSocketEvents.FS_FLOW_PERMANENTLY_DELETED,
  (data: { flow: SocketFsFlowDeleted }, dispatch) =>
    dispatch(flowPermanentlyRemoved(mapSocketFsFlowDeleted(data.flow).ns)),
)
